import { VA_FORM_IDS } from '@department-of-veterans-affairs/platform-forms/constants';
import {
  getTrackingDefaults,
  trackAction,
} from '../../../../shared/utils/tracking/datadogRumTracking';
import { isExpired } from '../../index';

// ──────────────────────────────────────────────
// Helper functions for data categorization
// ──────────────────────────────────────────────

/**
 * Categorize days remaining for BDD eligibility
 * Used to protect PII by grouping into ranges instead of exact values
 *
 * @param {number} days - Number of days remaining
 * @returns {string} Category label
 */
const categorizeDaysRemaining = days => {
  if (days <= 7) return '0-7-days';
  if (days <= 30) return '8-30-days';
  if (days <= 60) return '31-60-days';
  if (days <= 90) return '61-90-days';
  if (days <= 120) return '91-120-days';
  if (days <= 180) return '121-180-days';
  if (days <= 210) return '181-210-days';
  return '211-plus-days';
};

// ──────────────────────────────────────────────
// STEP VIEW TRACKING
// Track when users view each screener step (not URL page changes)
// ──────────────────────────────────────────────

/**
 * Track when a screener step is viewed
 * Also updates path history via React context
 *
 * @param {Object} params - Tracking parameters
 * @param {string} params.stepName - Name of the step being viewed
 * @param {Object} params.pathHistoryContext - Path history context from usePathHistory hook
 */
export const trackScreenerStepView = ({ stepName, pathHistoryContext }) => {
  // Get current history BEFORE adding the step
  const history = pathHistoryContext?.getHistory?.() || {};

  // Add step to path history (this is async, so do it after getting current state)
  if (pathHistoryContext?.addStep) {
    pathHistoryContext.addStep(stepName);
  }

  trackAction('21-526EZ_screener_stepViewed', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    stepName,

    // Path history for context (from BEFORE this step was added)
    startTime: history.startTime,
    pathSteps: history.path?.map(p => p.step) || [],
    pathTimestamps: history.path?.map(p => p.timestamp) || [],
    currentStep: history.path?.length || 0,
  });
};

// ──────────────────────────────────────────────
// IN-PROGRESS FORM TRACKING
// Track when the screener is first viewed with an existing in-progress form
// ──────────────────────────────────────────────

const hasInProgressForm = (savedForms = []) =>
  savedForms.some(
    savedForm =>
      savedForm.form === VA_FORM_IDS.FORM_21_526EZ &&
      !isExpired(savedForm.metadata),
  );

/**
 * Track when a user first views the screener and has an in-progress form
 *
 * @param {Object} params - Tracking parameters
 * @param {Array} params.savedForms - Array of saved forms from user profile
 */
export const trackScreenerViewedWithInProgressForm = ({ savedForms }) => {
  if (!hasInProgressForm(savedForms)) return;

  trackAction('21-526EZ_screener_viewedWithInProgressForm', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
  });
};

/**
 * Track when the introduction page sends a veteran with an in-progress form
 * straight into the application instead of the screener.
 *
 * This is the cohort the legacy auto-redirect got wrong: its saved-form check
 * read `savedForm.metaData?.expiresAt` (wrong key, and `isExpired` expects the
 * metadata object rather than the timestamp), so `hasSavedForm` was always
 * false and any veteran without a completed wizard status was bounced to
 * /get-started. Counting this event measures how often the new flow avoids
 * that redirect.
 *
 * @param {Object} params
 * @param {'v1'|'v2'} params.formVersion - Version of the in-progress form
 */
export const trackScreenerSkipForInProgressForm = ({ formVersion } = {}) => {
  trackAction('21-526EZ_screener_skippedForInProgressForm', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    formVersion,
  });
};

// ──────────────────────────────────────────────
// USER INPUT TRACKING
// Track final selections when continuing to next page
// ──────────────────────────────────────────────

/**
 * Track military status selection
 *
 * @param {string} militaryStatus - Status selected ('veteran' or 'active-duty')
 */
export const trackMilitaryStatusSelection = militaryStatus => {
  trackAction('21-526EZ_screener_militaryStatusSelected', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    pageName: 'military-status',
    fieldName: 'military-status',
    fieldValue: militaryStatus,
  });
};

/**
 * Track separation date entered (categorized for PII protection)
 *
 * @param {Object} params - Tracking parameters
 * @param {number} params.daysUntilSeparation - Number of days until separation date
 */
export const trackSeparationDateEntered = ({ daysUntilSeparation }) => {
  trackAction('21-526EZ_screener_separationDateEntered', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    pageName: 'release-date',
    fieldName: 'separation-date',
    daysRemainingCategory: categorizeDaysRemaining(daysUntilSeparation),
  });
};

/**
 * Track claim type selection
 *
 * @param {string} claimType - Type of claim selected ('new', 'increase' or 'both')
 */
export const trackClaimTypeSelection = claimType => {
  trackAction('21-526EZ_screener_claimTypeSelected', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    pageName: 'claim-type',
    fieldName: 'claim-type',
    fieldValue: claimType,
  });
};

/**
 * Track PACT Act eligibility selection
 *
 * @param {Object} params - Tracking parameters
 * @param {string} params.pactEligibility - Selection ('yes', 'not-sure' or 'no')
 * @param {boolean} params.isActiveDuty - Whether the user reached this page via
 *   the active-duty branch, which skips the claim-type question
 */
export const trackPactEligibilitySelection = ({
  pactEligibility,
  isActiveDuty,
}) => {
  trackAction('21-526EZ_screener_pactEligibilitySelected', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    pageName: 'pact-eligibility',
    fieldName: 'pact-eligibility',
    fieldValue: pactEligibility,
    isActiveDuty,
  });
};

// ──────────────────────────────────────────────
// TAILORED CONTENT TRACKING
// Track which variant of the evidence guidance a user was shown
// ──────────────────────────────────────────────

/**
 * Track the evidence guidance shown at the end of the screener
 *
 * @param {Object} params - Tracking parameters
 * @param {string} params.claimType - Claim type answer driving the evidence list
 * @param {string} params.pactEligibility - PACT Act eligibility answer
 * @param {boolean} params.showsPactContent - Whether PACT Act guidance was shown
 * @param {string} params.evidenceListVariant - 'increase-only' or 'full'
 */
export const trackEvidenceGuidanceDisplayed = ({
  claimType,
  pactEligibility,
  showsPactContent,
  evidenceListVariant,
}) => {
  trackAction('21-526EZ_screener_evidenceGuidanceDisplayed', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    pageName: 'evidence-needed',
    claimType,
    pactEligibility,
    showsPactContent,
    evidenceListVariant,
  });
};

// ──────────────────────────────────────────────
// LINK CLICK TRACKING
// ──────────────────────────────────────────────

/**
 * Track when user clicks a "Learn more" or informational link
 *
 * @param {Object} params - Tracking parameters
 * @param {string} params.linkLabel - Text of the link clicked
 * @param {string} params.pageName - Name of the page where link was clicked
 * @param {Object} params.pathHistoryData - Path history object from context
 */
export const trackExternalLinkClick = ({
  linkLabel,
  pageName,
  pathHistoryData,
}) => {
  const history = pathHistoryData || {};

  trackAction('21-526EZ_screener_externalLinkClicked', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    pageName,
    linkLabel,

    // Path history for context
    startTime: history.startTime,
    endTime: history.path?.[history.path.length - 1]?.timestamp,
    pathSteps: history.path?.map(p => p.step) || [],
    pathTimestamps: history.path?.map(p => p.timestamp) || [],
    totalSteps: history.path?.length || 0,
  });
};

// ──────────────────────────────────────────────
// COMPLETION TRACKING
// The screener hands off into Form 526EZ from a terminal page's Continue
// button rather than a "File a claim" link, so completion is its own event
// ──────────────────────────────────────────────

/**
 * Track when the user completes the screener and continues into Form 526EZ
 *
 * @param {Object} params - Tracking parameters
 * @param {string} params.pageName - Terminal page the user continued from
 * @param {string} params.outcome - Claim path the user was routed to ('bdd' or 'standard')
 * @param {string} params.militaryStatus - Military status answer
 * @param {string} params.claimType - Claim type answer
 * @param {string} params.pactEligibility - PACT Act eligibility answer
 */
export const trackScreenerCompleted = ({
  pageName,
  outcome,
  militaryStatus,
  claimType,
  pactEligibility,
}) => {
  trackAction('21-526EZ_screener_completed', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    pageName,
    outcome,
    militaryStatus,
    claimType,
    pactEligibility,
  });
};

// ──────────────────────────────────────────────
// RE-ENTRY TRACKING
// The Veteran pressed Back on the first form page and returned to the screener
// ──────────────────────────────────────────────

/**
 * Track when the user returns to the screener from the first form page
 *
 * @param {Object} params - Tracking parameters
 * @param {string} params.formVersion - Form version the Veteran was in ('v1' or 'v2')
 */
export const trackScreenerReenteredFromForm = ({ formVersion } = {}) => {
  const { sourcePath } = getTrackingDefaults();
  trackAction('21-526EZ_screener_reenteredFromForm', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    formVersion,
    sourcePath,
  });
};

// ──────────────────────────────────────────────
// ERROR TRACKING
// Track validation errors encountered
// ──────────────────────────────────────────────

/**
 * Track validation errors in screener pages
 *
 * @param {Object} params - Tracking parameters
 * @param {string} params.pageName - Page where error occurred
 * @param {string} params.fieldName - Field that triggered the error
 * @param {string} params.errorMessage - Error message displayed to user
 */
export const trackScreenerValidationError = ({
  pageName,
  fieldName,
  errorMessage,
}) => {
  trackAction('21-526EZ_screener_validationError', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    pageName,
    fieldName,
    errorType: 'validation',
    errorMessage,
  });
};

// ──────────────────────────────────────────────
// BDD-SPECIFIC TRACKING
// Track BDD eligibility information and warnings
// ──────────────────────────────────────────────

/**
 * Track when BDD eligibility message is displayed to user
 *
 * @param {Object} params - Tracking parameters
 * @param {number} params.daysRemaining - Number of days remaining to file BDD claim
 * @param {boolean} params.isLastDay - Whether this is the last day to file
 */
export const trackBddEligibilityDisplayed = ({ daysRemaining, isLastDay }) => {
  trackAction('21-526EZ_screener_BDDEligibilityDisplayed', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    daysRemainingCategory: categorizeDaysRemaining(daysRemaining),
    isLastDay,
    eligibilityType: 'bdd',
  });
};

/**
 * Track when "unable to file BDD" message is displayed
 *
 * @param {Object} params - Tracking parameters
 * @param {string} params.reason - Reason user cannot file BDD ('too-early')
 * @param {number} params.daysRemaining - Number of days remaining until separation
 */
export const trackUnableToFileBddDisplayed = ({ reason, daysRemaining }) => {
  trackAction('21-526EZ_screener_unableToFileBDDDisplayed', {
    formId: VA_FORM_IDS.FORM_21_526EZ,
    reason,
    daysRemainingCategory: categorizeDaysRemaining(daysRemaining),
    eligibilityType: 'not-eligible',
  });
};
